import { useState } from 'react';
import { debugLog } from '../../../../configs/loggingConfig';

/**
 * Template-specific property tracking system
 * 
 * This system tracks changes to template properties that have their own API endpoints
 * for adding/removing items (tags, skills, resources, dataStores, etc.)
 * 
 * KEY CONCEPT: UI State vs Backend Payload
 * - The chatTemplate state contains ALL properties for UI display (tags, skills, data, etc.)
 * - When sending to backend, we use createCleanTemplatePayload() to remove properties
 *   that have separate API endpoints to avoid serialization issues
 * - These removed properties are handled via the change tracking system
 *
 * HOW TO ADD NEW PROPERTIES:
 * 1. Add the property to propertyChanges state below (e.g., "newProperty": { added: [], removed: [] })
 * 2. Add the property to createCleanTemplatePayload() function to exclude it from backend payload
 * 3. Add corresponding API calls in applyPropertyChanges function
 * 4. Update the resetPropertyChanges function to include the new property
 * 5. Use trackPropertyChange() in your components when properties change
 * 6. The system will automatically apply changes when createTemplate/updateTemplate is called
 *
 * EXAMPLE USAGE IN COMPONENTS:
 * - When adding: trackPropertyChange('tags', 'added', ['newTag'])
 * - When removing: trackPropertyChange('tags', 'removed', ['oldTag'])
 * - For objects: trackPropertyChange('dataStores', 'added', [dataStoreObject])
 * - For IDs: trackPropertyChange('resources', 'removed', [resourceId])
 * - For quick actions: trackPropertyChange('quickActions', 'added', [quickActionObject])
 *
 * CURRENTLY SUPPORTED PROPERTIES:
 * - tags: String array (tag names)
 * - skills: String array (skill IDs)
 * - resources: File objects for additions, resource IDs for removals
 * - dataStores: DataStore objects
 * - quickActions: QuickAction objects for additions/updates, QuickAction objects for removals
 *
 * PROPERTIES EXCLUDED FROM BACKEND PAYLOAD:
 * - includedFiles, tags, skills, data, accesses, chatTemplateShortcuts (handled via separate API calls)
 */
export const useTemplatePropertyTracker = (apiServiceClient) => {
    // Track changes to template properties that have separate API endpoints
    const [propertyChanges, setPropertyChanges] = useState({
        tags: { added: [], removed: [] },
        skills: { added: [], removed: [] },
        resources: { added: [], removed: [] },
        dataStores: { added: [], removed: [] },
        quickActions: { added: [], updated: [], removed: [] }
    });
    
    // Store original template state for comparison
    const [originalTemplate, setOriginalTemplate] = useState(null);

    /**
     * Get unique identifier for any item
     * @param {string} propertyType - The type of property
     * @param {*} item - The item to get identifier for
     * @returns {*} The unique identifier
     */
    const getItemKey = (propertyType, item) => {
        if (item == null) return null;
        if (typeof item !== 'object') return item;
        
        switch (propertyType) {
            case 'resources':
                return item.resourceId || item.id || `${item.name || 'unknown'}_${item.size || 0}`;
            case 'dataStores':
                return item.dataCollectionId || item.id;
            case 'quickActions':
                return item.id;
            case 'tags':
            case 'skills':
                return item.id || item.name || item;
            default:
                return item.id || item.name || item;
        }
    };

    /**
     * Track changes to template properties
     * @param {string} propertyType - The type of property being tracked
     * @param {string} action - The action being performed ('added', 'removed', 'updated')
     * @param {Array} items - The items being added/removed/updated
     */
    const trackPropertyChange = (propertyType, action, items) => {
        setPropertyChanges(prev => {
            const currentItems = prev[propertyType][action] || [];
            
            // Filter out duplicates
            const uniqueItems = items.filter(newItem => {
                const newKey = getItemKey(propertyType, newItem);
                return !currentItems.some(existing => 
                    getItemKey(propertyType, existing) === newKey
                );
            });

            // Create new state with unique items added
            const newState = {
                ...prev,
                [propertyType]: {
                    ...prev[propertyType],
                    [action]: [...currentItems, ...uniqueItems]
                }
            };

            // Handle cross-actions (added vs removed)
            const opposingAction = action === 'added' ? 'removed' : 
                                 action === 'removed' ? 'added' : null;
            
            if (opposingAction) {
                const opposingItems = newState[propertyType][opposingAction];
                newState[propertyType][opposingAction] = opposingItems.filter(opposingItem => {
                    const opposingKey = getItemKey(propertyType, opposingItem);
                    return !uniqueItems.some(newItem => 
                        getItemKey(propertyType, newItem) === opposingKey
                    );
                });
            }

            return newState;
        });
    };

    /**
     * Reset change tracking to initial state
     */
    const resetPropertyChanges = () => {
        setPropertyChanges({
            tags: { added: [], removed: [] },
            skills: { added: [], removed: [] },
            resources: { added: [], removed: [] },
            dataStores: { added: [], removed: [] },
            quickActions: { added: [], updated: [], removed: [] }
        });
    };

    /**
     * Get items that were added/removed for a property type
     * @param {string} propertyType - The type of property to get changes for
     * @returns {Object} Object containing added and removed arrays
     */
    const getPropertyChanges = (propertyType) => {
        return propertyChanges[propertyType];
    };

    /**
     * Check if there are any pending changes across all property types
     * @returns {boolean} True if there are pending changes
     */
    const hasPendingChanges = () => {
        return Object.values(propertyChanges).some(propertyChange => 
            Object.values(propertyChange).some(actionArray => actionArray.length > 0)
        );
    };

    /**
     * Create a clean payload for backend API calls by removing properties that have separate endpoints
     * @param {Object} templateObj - The template object to clean
     * @returns {Object} Clean template payload for backend
     */
    const createCleanTemplatePayload = (templateObj) => {
        const { 
            includedFiles, 
            tags, 
            skills, 
            data, 
            accesses, 
            chatTemplateShortcuts,
            ...cleanPayload 
        } = templateObj;
        
        return cleanPayload;
    };

    /**
     * Apply all tracked property changes via API calls
     * @param {string} templateId - The template ID to apply changes to
     */
    const applyPropertyChanges = async (templateId) => {
        const changes = propertyChanges;
        
        try {
            // Apply tag changes
            if (changes.tags.added.length > 0) {
                debugLog.log("[TEMPLATE] Adding tags:", changes.tags.added);
                await apiServiceClient.Templates.addTemplateTags(templateId, changes.tags.added);
            }
            if (changes.tags.removed.length > 0) {
                debugLog.log("[TEMPLATE] Removing tags:", changes.tags.removed);
                await apiServiceClient.Templates.removeTemplateTags(templateId, changes.tags.removed);
            }
            
            // Apply skill changes
            if (changes.skills.added.length > 0) {
                debugLog.log("[TEMPLATE] Adding skills:", changes.skills.added);
                await apiServiceClient.Templates.addTemplateSkills(templateId, changes.skills.added);
            }
            if (changes.skills.removed.length > 0) {
                debugLog.log("[TEMPLATE] Removing skills:", changes.skills.removed);
                await apiServiceClient.Templates.removeTemplateSkills(templateId, changes.skills.removed);
            }
            
            // Apply resource changes
            if (changes.resources.added.length > 0) {
                debugLog.log("[TEMPLATE] Adding resources:", changes.resources.added);
                await Promise.all(changes.resources.added.map(file => 
                    apiServiceClient.Templates.addFileToTemplate(templateId, file)
                ));
            }
            if (changes.resources.removed.length > 0) {
                debugLog.log("[TEMPLATE] Removing resources:", changes.resources.removed);
                await Promise.all(changes.resources.removed.map(resourceId => 
                    apiServiceClient.Templates.deleteFileFromTemplate(templateId, resourceId)
                ));
            }
            
            // Apply dataStore changes
            if (changes.dataStores.added.length > 0) {
                debugLog.log("[TEMPLATE] Adding dataStores:", changes.dataStores.added);
                await Promise.all(changes.dataStores.added.map(dataStore => 
                    apiServiceClient.Templates.updateDataStoreStatus(templateId, dataStore.id, true)
                ));
            }
            if (changes.dataStores.removed.length > 0) {
                debugLog.log("[TEMPLATE] Removing dataStores:", changes.dataStores.removed);
                await Promise.all(changes.dataStores.removed.map(dataStore => 
                    apiServiceClient.Templates.updateDataStoreStatus(templateId, dataStore.dataCollectionId, false)
                ));
            }

            // Apply quick action changes
            if (changes.quickActions.added.length > 0) {
                debugLog.log("[TEMPLATE] Adding quick actions:", changes.quickActions.added);
                await Promise.all(changes.quickActions.added.map(action => 
                    apiServiceClient.QuickActions.createQuickAction({ ...action, principleId: templateId })
                ));
            }
            if (changes.quickActions.updated.length > 0) {
                debugLog.log("[TEMPLATE] Updating quick actions:", changes.quickActions.updated);
                await Promise.all(changes.quickActions.updated.map(action => 
                    apiServiceClient.QuickActions.updateQuickAction(action.id, action)
                ));
            }
            if (changes.quickActions.removed.length > 0) {
                debugLog.log("[TEMPLATE] Removing quick actions:", changes.quickActions.removed);
                await Promise.all(changes.quickActions.removed.map(actionId => 
                    apiServiceClient.QuickActions.deleteQuickAction(actionId)
                ));
            }
            
            // Reset changes after successful application
            resetPropertyChanges();
            setOriginalTemplate(null);
            
        } catch (error) {
            debugLog.error("[TEMPLATE] Error applying property changes:", error);
            throw error;
        }
    };

    return {
        propertyChanges,
        originalTemplate,
        setOriginalTemplate,
        trackPropertyChange,
        resetPropertyChanges,
        getPropertyChanges,
        applyPropertyChanges,
        createCleanTemplatePayload,
        hasPendingChanges, // a lil extra maybe used to telling user they have changes
    };
};